import { useFormik } from 'formik';
import React, { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { decryptData, encryptData } from '../Utils/encryption';
import { updateUser } from '../Redux/Slice/user.slice';
import { FaCamera, FaEdit, FaSave, FaTimes, FaUser } from 'react-icons/fa';
import { IMAGE_URL } from '../Utils/baseUrl';
import { Modal } from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';

export default function AdminProfile() {
    const userId = localStorage.getItem("yoyouserId");
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const currentUser = useSelector((state) => state.user.currUser);

    const [isEditing, setIsEditing] = useState(false);
    const [originalValues, setOriginalValues] = useState({});
    const [selectedFile, setSelectedFile] = useState(null);
    const [previewUrl, setPreviewUrl] = useState(null);
    const [imageRemoved, setImageRemoved] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);

    const formik = useFormik({
        initialValues: {
            userName: "",
            fullName: "",
            email: "",
            photo: "",
        },
        onSubmit: (values) => {
            const encryptedValues = {
                userName: values.userName ? encryptData(values.userName) : "",
                fullName: values.fullName ? encryptData(values.fullName) : "",
                email: values.email ? encryptData(values.email) : "",
                photo: values.photo || "",
            };

            dispatch(
                updateUser({
                    id: userId,
                    values: encryptedValues,
                    file: selectedFile,
                })
            ).then((response) => {
                if (response.payload?.success) {
                    setIsEditing(false);
                    setOriginalValues(values);
                    navigate("/admin/profile");
                    setSelectedFile(null);
                    setPreviewUrl(null);
                    setImageRemoved(false);
                }
            });
        },
    });

    const {
        handleSubmit,
        setFieldValue,
        handleChange,
        setValues,
        values,
    } = formik;

    const isChanged = useMemo(() => {
        return (
            JSON.stringify(values) !== JSON.stringify(originalValues) ||
            selectedFile !== null ||
            imageRemoved
        );
    }, [values, originalValues, selectedFile, imageRemoved]);

    useEffect(() => {
        if (currentUser) {
            const userData = {
                userName: decryptData(currentUser.userName) || "",
                fullName: decryptData(currentUser.fullName) || "",
                email: decryptData(currentUser.email) || "",
                photo: currentUser.photo || "",
            };
            setValues(userData);
            setOriginalValues(userData);
        }
    }, [currentUser, setValues]);

    const handleImageUpload = (e) => {
        const file = e.target.files[0];
        if (file) {
            setSelectedFile(file);
            setImageRemoved(false);
            setIsEditing(true);

            const reader = new FileReader();
            reader.onloadend = () => {
                setPreviewUrl(reader.result);
                setFieldValue("photo", reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleRemoveImage = () => {
        setSelectedFile(null);
        setPreviewUrl(null);
        setImageRemoved(true);
        setFieldValue("photo", "null");
    };

    const avatars = [
        require("../Asset/images/1.png"),
        require("../Asset/images/2.png"),
        require("../Asset/images/3.png"),
        require("../Asset/images/4.png"),
        require("../Asset/images/5.png"),
        require("../Asset/images/6.png"),
        require("../Asset/images/7.png"),
        require("../Asset/images/8.png"),
    ];

    const handleAvatarSelect = async (avatar) => {
        try {
            const response = await fetch(avatar);
            const blob = await response.blob();
            const fileName = `avatar-${Date.now()}.png`;
            const file = new File([blob], fileName, { type: blob.type });

            setIsEditing(true);
            setPreviewUrl(avatar);
            setSelectedFile(file);
            setFieldValue("photo", file);
            setImageRemoved(false);
            setIsModalOpen(false);
        } catch (error) {
            console.error("Error selecting avatar:", error);
        }
    };

    const handleEditClick = () => {
        if (!isEditing) {
            setIsEditing(true);
        } else {
            if (isChanged) {
                handleSubmit();
            } else {
                setValues(originalValues);
                setIsEditing(false);
            }
        }
    };

    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-4xl mx-auto"
        >
            <div className="mb-8">
                <h1 className="text-3xl font-bold text-white tracking-tight">Admin Profile</h1>
                <p className="text-gray-400">Manage your account information and preferences.</p>
            </div>

            <div className="bg-white/5 border border-white/10 rounded-3xl p-8 backdrop-blur-sm relative overflow-hidden">
                <div className="absolute top-0 right-0 p-8 opacity-5">
                    <FaUser className="text-9xl text-primary" />
                </div>

                <form onSubmit={handleSubmit} className="relative z-10 flex flex-col md:flex-row gap-12">
                    {/* Left: Profile Image */}
                    <div className="flex flex-col items-center gap-6">
                        <div className="relative group">
                            <div className="w-40 h-40 rounded-3xl overflow-hidden border-2 border-primary/20 bg-primary/10 shadow-[0_0_30px_rgba(98,99,237,0.2)] transition-all duration-500 group-hover:border-primary/50">
                                {previewUrl ? (
                                    <img src={previewUrl} alt="" className="w-full h-full object-cover" />
                                ) : !imageRemoved && currentUser?.photo && currentUser?.photo !== "null" ? (
                                    <img 
                                        src={currentUser.photo.startsWith("/uploads/") ? `${IMAGE_URL}${currentUser.photo}` : currentUser.photo} 
                                        alt="" 
                                        className="w-full h-full object-cover" 
                                    />
                                ) : (
                                    <div className="w-full h-full flex items-center justify-center text-5xl font-bold text-primary">
                                        {values.fullName?.[0] || values.userName?.[0] || "A"}
                                    </div>
                                )}
                            </div>
                            
                            <motion.button
                                whileHover={{ scale: 1.1 }}
                                whileTap={{ scale: 0.9 }}
                                type="button"
                                onClick={() => setIsModalOpen(true)}
                                className="absolute -bottom-3 -right-3 w-12 h-12 rounded-2xl bg-gradient-primary text-white flex items-center justify-center shadow-lg border border-white/20"
                            >
                                <FaCamera />
                            </motion.button>

                            {(previewUrl || (currentUser?.photo && currentUser?.photo !== "null")) && (
                                <button
                                    type="button"
                                    onClick={handleRemoveImage}
                                    className="absolute -top-3 -right-3 w-8 h-8 rounded-xl bg-red-500 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow-lg"
                                >
                                    <FaTimes size={12} />
                                </button>
                            )}
                        </div>
                        <p className="text-xs font-bold text-gray-500 uppercase tracking-widest">Profile Picture</p>
                    </div>

                    {/* Right: Fields */}
                    <div className="flex-1 space-y-6">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                            <div className="space-y-2">
                                <label className="text-xs font-bold text-gray-500 uppercase tracking-widest ml-1">Full Name</label>
                                <input
                                    name="fullName"
                                    type="text"
                                    className="w-full bg-white/5 border border-white/10 rounded-2xl px-4 py-3.5 text-white focus:border-primary/50 focus:ring-1 focus:ring-primary/50 outline-none transition-all disabled:opacity-50"
                                    onChange={handleChange}
                                    value={values.fullName}
                                    disabled={!isEditing}
                                />
                            </div>
                            <div className="space-y-2">
                                <label className="text-xs font-bold text-gray-500 uppercase tracking-widest ml-1">Username</label>
                                <input
                                    name="userName"
                                    type="text"
                                    className="w-full bg-white/5 border border-white/10 rounded-2xl px-4 py-3.5 text-white/50 cursor-not-allowed outline-none"
                                    value={values.userName}
                                    disabled
                                />
                            </div>
                        </div>

                        <div className="space-y-2">
                            <label className="text-xs font-bold text-gray-500 uppercase tracking-widest ml-1">Email Address</label>
                            <input
                                name="email"
                                type="email"
                                className="w-full bg-white/5 border border-white/10 rounded-2xl px-4 py-3.5 text-white/50 cursor-not-allowed outline-none"
                                value={values.email}
                                disabled
                            />
                        </div>

                        <div className="pt-6 flex gap-4">
                            {!isEditing ? (
                                <motion.button
                                    whileHover={{ scale: 1.02 }}
                                    whileTap={{ scale: 0.98 }}
                                    type="button"
                                    onClick={() => setIsEditing(true)}
                                    className="px-8 py-3.5 rounded-2xl bg-white/5 border border-white/10 text-white font-bold flex items-center gap-2 hover:bg-white/10 transition-all"
                                >
                                    <FaEdit /> Edit Profile
                                </motion.button>
                            ) : (
                                <>
                                    <motion.button
                                        whileHover={{ scale: 1.02 }}
                                        whileTap={{ scale: 0.98 }}
                                        type="submit"
                                        className={`px-8 py-3.5 rounded-2xl font-bold flex items-center gap-2 shadow-lg transition-all ${isChanged ? 'bg-gradient-primary text-white shadow-primary/20' : 'bg-gray-700 text-gray-400 cursor-not-allowed'}`}
                                        disabled={!isChanged}
                                    >
                                        <FaSave /> Save Changes
                                    </motion.button>
                                    <motion.button
                                        whileHover={{ scale: 1.02 }}
                                        whileTap={{ scale: 0.98 }}
                                        type="button"
                                        onClick={() => {
                                            setValues(originalValues);
                                            setIsEditing(false);
                                            setPreviewUrl(null);
                                            setSelectedFile(null);
                                        }}
                                        className="px-8 py-3.5 rounded-2xl bg-white/5 border border-white/10 text-white font-bold flex items-center gap-2 hover:bg-red-500/10 hover:text-red-400 hover:border-red-500/20 transition-all"
                                    >
                                        <FaTimes /> Cancel
                                    </motion.button>
                                </>
                            )}
                        </div>
                    </div>
                </form>
            </div>

            {/* Avatar Selection Modal */}
            <Modal
                open={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50"
            >
                <div className="bg-[#141414] border border-white/10 rounded-3xl p-8 text-white w-[90%] max-w-[500px] shadow-2xl overflow-hidden">
                    <div className="flex justify-between items-center mb-8">
                        <h2 className="text-xl font-bold tracking-tight">Choose Avatar</h2>
                        <button onClick={() => setIsModalOpen(false)} className="text-gray-400 hover:text-white transition-colors">
                            <FaTimes size={20} />
                        </button>
                    </div>

                    <div className="space-y-8">
                        <div>
                            <label
                                htmlFor="modalProfileImageInput"
                                className="flex flex-col items-center justify-center cursor-pointer border-2 border-dashed border-white/10 rounded-2xl p-6 hover:bg-white/5 hover:border-primary/50 transition-all group"
                            >
                                <div className="w-12 h-12 rounded-xl bg-primary/10 text-primary flex items-center justify-center mb-4 group-hover:bg-gradient-primary group-hover:text-white transition-all">
                                    <FaCamera />
                                </div>
                                <span className="font-bold text-sm mb-1">Upload from computer</span>
                                <span className="text-xs text-gray-500">JPG, PNG or GIF • Max 5MB</span>
                                <input
                                    id="modalProfileImageInput"
                                    type="file"
                                    accept="image/*"
                                    className="hidden"
                                    onChange={(e) => {
                                        handleImageUpload(e);
                                        setIsModalOpen(false);
                                    }}
                                />
                            </label>
                        </div>

                        <div className="grid grid-cols-4 gap-4">
                            {avatars.map((avatar, index) => (
                                <motion.div
                                    key={index}
                                    whileHover={{ scale: 1.05 }}
                                    whileTap={{ scale: 0.95 }}
                                    className="relative cursor-pointer group"
                                    onClick={() => handleAvatarSelect(avatar)}
                                >
                                    <div className="w-full aspect-square rounded-2xl overflow-hidden border-2 border-transparent group-hover:border-primary transition-all">
                                        <img src={avatar} alt="" className="w-full h-full object-cover" />
                                    </div>
                                </motion.div>
                            ))}
                        </div>
                    </div>
                </div>
            </Modal>
        </motion.div>
    )
}